import { FC, useMemo } from 'react';
import { css } from '@emotion/react';
import { CallIntelConstant, CallIntelligenceV2, CallIntelUtils } from '@frontend/api-call-intel';
import { Trans, useTranslation } from '@frontend/i18n';
import { sentry } from '@frontend/tracking';
import { theme } from '@frontend/theme';
import { Heading, Text } from '@frontend/design-system';
import { useActiveFilters } from '../../hooks/use-active-filters';
import { ActiveOpportunitiesCard } from './active-opportunities-card';
import { ConversionDuringCallCard } from './conversion-during-call-card';

export const SchedulingOverview: FC = () => {
  const { t } = useTranslation('call-intelligence');

  const { filters } = useActiveFilters();

  const schedulingAggregateStatsRequestPayload = useMemo(
    () => CallIntelUtils.generateChartsPayload({ filters }),
    [filters]
  );

  const { data: schedulingAggregateStats } = CallIntelligenceV2.Queries.useGetSchedulingAggregateStatsQuery({
    request: schedulingAggregateStatsRequestPayload,
    options: {
      enabled: !!filters.locations?.length,
      retry: false,
      cacheTime: CallIntelConstant.DEFAULT_CACHE_TIME,
      staleTime: CallIntelConstant.DEFAULT_CACHE_TIME,
      onError: (error) => {
        sentry.warn({
          team: 'analytics',
          topic: 'call-intelligence',
          error: `Error fetching scheduling aggregate stats: ${error}`,
          addContext: {
            name: 'filters',
            context: { payload: schedulingAggregateStatsRequestPayload },
          },
        });
      },
    },
  });

  const totalCallsAnalyzed = schedulingAggregateStats?.current?.totalCallsAnalyzed || 0;

  return (
    <section css={defaultStyles.container} data-testid='scheduling-overview-section'>
      <div css={defaultStyles.headerContainer}>
        <Heading level={3}>{t('Scheduling')}</Heading>
        <Text size='medium' color='subdued'>
          <Trans t={t} count={totalCallsAnalyzed} values={{ totalCallsAnalyzed }}>
            <strong>{{ totalCallsAnalyzed }}</strong> calls analyzed
          </Trans>
        </Text>
      </div>
      <div css={defaultStyles.cardContainer}>
        <ActiveOpportunitiesCard schedulingAggregateStats={schedulingAggregateStats?.current} />
        <ConversionDuringCallCard schedulingAggregateStats={schedulingAggregateStats?.current} />
      </div>
    </section>
  );
};

const defaultStyles = {
  container: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
  `,
  headerContainer: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
  `,
  cardContainer: css`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: ${theme.spacing(3)};
  `,
};
